<template>
  <div class="certificate-holder-report">
    <!-- 세그먼트 헤더 -->
    <a-alert
      message="👥 인증서 보유자 (신규 + 기존)"
      description="목적: 후기 작성 유도 + 바이럴 확산 | 경로: 인증서 알림톡/후기 알림톡 → 후기 작성 → 공유"
      type="info"
      show-icon
      class="segment-header"
    />

    <!-- 핵심 지표 카드 -->
    <a-row :gutter="[16, 16]" class="metrics-section">
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card class="metric-card zeiss-blue">
          <a-tooltip placement="top">
            <template #title>
              <div class="kpi-tooltip">
                <p><strong>메시지 발송</strong></p>
                <p>• 인증서 알림톡 + 후기 알림톡</p>
                <p>• 이벤트 기간 내 발송된 총 메시지 수</p>
                <p v-if="hasThermageData">• 전체: {{ formatNumber(metrics.totalSent) }}</p>
                <p v-if="hasThermageData">• 써마지: {{ formatNumber(thermageMetrics.totalSent) }} ({{ formatPercentage(thermageMetrics.totalSent / metrics.totalSent * 100) }})</p>
              </div>
            </template>
            <a-statistic
              title="메시지 발송"
              :value="metrics.totalSent"
              :value-style="{ color: '#005EB8', fontSize: '24px', fontWeight: '700' }"
            >
              <template #prefix><MailOutlined /></template>
            </a-statistic>
          </a-tooltip>
          <div class="metric-detail">누적 발송 건수</div>
          <div v-if="hasThermageData" class="metric-thermage">
            써마지: {{ formatNumber(thermageMetrics.totalSent) }} ({{ formatPercentage(thermageMetrics.totalSent / metrics.totalSent * 100) }})
          </div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card class="metric-card zeiss-green">
          <a-tooltip placement="top">
            <template #title>
              <div class="kpi-tooltip">
                <p><strong>클릭률 (CTR)</strong></p>
                <p>• 계산식: (클릭수 ÷ 발송수) × 100</p>
                <p>• 메시지를 받고 링크를 클릭한 비율</p>
                <p>• 메시지 매력도 지표</p>
              </div>
            </template>
            <a-statistic
              title="클릭률 (CTR)"
              :value="metrics.clickRate"
              suffix="%"
              :precision="2"
              :value-style="{ color: '#20C997', fontSize: '24px', fontWeight: '700' }"
            >
              <template #prefix><EyeOutlined /></template>
            </a-statistic>
          </a-tooltip>
          <div class="metric-detail">{{ formatNumber(metrics.totalClicks) }}명 클릭</div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card class="metric-card zeiss-orange">
          <a-tooltip placement="top">
            <template #title>
              <div class="kpi-tooltip">
                <p><strong>후기 작성률</strong></p>
                <p>• 계산식: (후기작성 ÷ 발송수) × 100</p>
                <p>• 메시지를 받고 후기를 작성한 비율</p>
                <p>• 전환의 핵심 지표</p>
              </div>
            </template>
            <a-statistic
              title="후기 작성률"
              :value="metrics.reviewRate"
              suffix="%"
              :precision="2"
              :value-style="{ color: '#faad14', fontSize: '24px', fontWeight: '700' }"
            >
              <template #prefix><EditOutlined /></template>
            </a-statistic>
          </a-tooltip>
          <div class="metric-detail">{{ formatNumber(metrics.totalReviews) }}개 후기</div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card class="metric-card zeiss-red">
          <a-tooltip placement="top">
            <template #title>
              <div class="kpi-tooltip">
                <p><strong>바이럴 공유율</strong></p>
                <p>• 계산식: (공유자 ÷ 발송수) × 100</p>
                <p>• 후기 작성 후 공유한 고객 비율</p>
                <p>• 바이럴 확산력 지표</p>
              </div>
            </template>
            <a-statistic
              title="바이럴 공유율"
              :value="metrics.shareRate"
              suffix="%"
              :precision="2"
              :value-style="{ color: '#E03131', fontSize: '24px', fontWeight: '700' }"
            >
              <template #prefix><ShareAltOutlined /></template>
            </a-statistic>
          </a-tooltip>
          <div class="metric-detail">{{ formatNumber(metrics.totalShares) }}명 공유</div>
        </a-card>
      </a-col>
    </a-row>

    <!-- 후기 작성 퍼널 -->
    <a-card title="📝 후기 작성 퍼널 (발송 → 클릭 → 페이지 유입 → 후기 작성 → 공유)" class="funnel-card">
      <div class="funnel-horizontal">
        <div class="funnel-steps">
          <div
            v-for="(step, index) in reviewFunnel"
            :key="index"
            class="funnel-step-item"
          >
            <a-tooltip placement="top">
              <template #title>
                <div class="funnel-tooltip">
                  <p><strong>{{ step.label }}</strong></p>
                  <p>• 건수: {{ formatNumber(step.value) }}</p>
                  <p v-if="index > 0">• 전환율: {{ formatPercentage(step.conversionRate) }}</p>
                  <p v-if="index > 0">• 이전 단계 대비 {{ formatPercentage(step.conversionRate) }} 전환</p>
                </div>
              </template>
              <div class="funnel-step-box" :style="{ backgroundColor: step.color }">
                <div class="step-number">{{ index + 1 }}</div>
                <div class="step-label-text">{{ step.label }}</div>
                <div class="step-value-text">{{ formatNumber(step.value) }}</div>
              </div>
            </a-tooltip>

            <!-- 전환율 화살표 -->
            <div v-if="index < reviewFunnel.length - 1" class="funnel-connector">
              <div class="connector-line"></div>
              <div class="connector-rate">
                <span class="rate-value">{{ formatPercentage(reviewFunnel[index + 1].conversionRate) }}</span>
                <RightOutlined class="rate-arrow" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-card>

    <!-- 바이럴 지표 -->
    <a-row :gutter="[16, 16]">
      <a-col :xs="24" :lg="12">
        <a-card title="🔄 바이럴 확산" class="viral-card">
          <a-row :gutter="16">
            <a-col :span="12">
              <a-statistic
                title="공유자 수"
                :value="metrics.totalShares"
              >
                <template #prefix><ShareAltOutlined style="color: #faad14" /></template>
              </a-statistic>
            </a-col>
            <a-col :span="12">
              <a-statistic
                title="퀴즈참여 유도"
                :value="metrics.totalReferrals"
              >
                <template #prefix><UserAddOutlined style="color: #52c41a" /></template>
              </a-statistic>
            </a-col>
            <a-col :span="24" style="margin-top: 16px">
              <div class="viral-insight">
                <span class="insight-label">바이럴 계수:</span>
                <span class="insight-value">{{ viralCoefficient }}</span>
                <span class="insight-desc">(공유자 1명당 유도한 퀴즈참여자)</span>
              </div>
            </a-col>
          </a-row>
        </a-card>
      </a-col>
      <a-col :xs="24" :lg="12">
        <a-card title="🎁 리워드 지표" class="reward-card">
          <a-row :gutter="16">
            <a-col :span="12">
              <a-statistic
                title="쿠폰 발급"
                :value="metrics.totalCoupons"
              >
                <template #prefix><GiftOutlined style="color: #f5222d" /></template>
              </a-statistic>
            </a-col>
            <a-col :span="12">
              <a-statistic
                title="발급률"
                :value="metrics.couponRate"
                suffix="%"
                :precision="2"
              >
                <template #prefix><PercentageOutlined style="color: #1890ff" /></template>
              </a-statistic>
            </a-col>
            <a-col :span="24" style="margin-top: 16px">
              <div class="reward-detail">
                <p>• 후기 작성 쿠폰: 최대 1장</p>
                <p>• 공유 5회 쿠폰: 최대 1장</p>
                <p>• 1인당 최대 2장</p>
              </div>
            </a-col>
          </a-row>
        </a-card>
      </a-col>
    </a-row>

    <!-- 세그먼트별 상세 데이터 -->
    <a-card title="📊 세그먼트별 상세 (신규 vs 기존)" class="detail-card">
      <a-tabs v-model:activeKey="activeMessageType" type="line">
        <a-tab-pane key="certificate" tab="✨ 신규 인증서 발급자">
          <a-table
            :columns="detailColumns"
            :data-source="certificateData"
            :pagination="{ pageSize: 10 }"
            :scroll="{ x: 1400 }"
            size="small"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'date'">
                <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
              </template>
              <template v-else-if="['sent', 'clicks', 'eventPageVisits', 'reviewSubmissions', 'shares', 'couponIssuances'].includes(column.key)">
                <span>{{ formatNumber(record[column.key]) }}</span>
              </template>
              <template v-else-if="column.key === 'clickRate'">
                <span>{{ formatPercentage(record.clickRate) }}</span>
              </template>
            </template>
          </a-table>
        </a-tab-pane>
        <a-tab-pane key="review" tab="🔄 기존 인증서 보유자">
          <a-table
            :columns="detailColumns"
            :data-source="reviewData"
            :pagination="{ pageSize: 10 }"
            :scroll="{ x: 1400 }"
            size="small"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'date'">
                <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
              </template>
              <template v-else-if="['sent', 'clicks', 'eventPageVisits', 'reviewSubmissions', 'shares', 'couponIssuances'].includes(column.key)">
                <span>{{ formatNumber(record[column.key]) }}</span>
              </template>
              <template v-else-if="column.key === 'clickRate'">
                <span>{{ formatPercentage(record.clickRate) }}</span>
              </template>
            </template>
          </a-table>
        </a-tab-pane>
      </a-tabs>
    </a-card>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { Card, Row, Col, Statistic, Alert, Table, Tabs, Tooltip } from 'ant-design-vue';
import {
  MailOutlined,
  EyeOutlined,
  EditOutlined,
  ShareAltOutlined,
  DownOutlined,
  RightOutlined,
  UserAddOutlined,
  GiftOutlined,
  PercentageOutlined,
} from '@ant-design/icons-vue';
import { formatNumber, formatPercentage, formatDate, getDayOfWeek } from '../utils';

export default {
  name: 'CertificateHolderReport',
  components: {
    ACard: Card,
    ARow: Row,
    ACol: Col,
    AStatistic: Statistic,
    AAlert: Alert,
    ATable: Table,
    ATabs: Tabs,
    ATabPane: Tabs.TabPane,
    ATooltip: Tooltip,
    MailOutlined,
    EyeOutlined,
    EditOutlined,
    ShareAltOutlined,
    DownOutlined,
    RightOutlined,
    UserAddOutlined,
    GiftOutlined,
    PercentageOutlined,
  },
  props: {
    reportData: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const activeMessageType = ref('certificate');

    // 인증서 보유자 데이터 필터링 (certificate + review 타입)
    const certificateHolderData = computed(() => {
      return props.reportData.filter(
        item => item.messageType === 'certificate' || item.messageType === 'review'
      );
    });

    // THERMAGE 데이터 존재 여부 확인
    const hasThermageData = computed(() => {
      return certificateHolderData.value.some(item => item.thermage);
    });

    // 핵심 지표 계산
    const metrics = computed(() => {
      const data = certificateHolderData.value;
      const totalSent = data.reduce((sum, item) => sum + (item.sent || 0), 0);
      const totalClicks = data.reduce((sum, item) => sum + (item.uniqueClicks || 0), 0);
      const totalReviews = data.reduce((sum, item) => sum + (item.reviewSubmissions || 0), 0);
      const totalShares = data.reduce((sum, item) => sum + (item.shares || 0), 0);
      const totalReferrals = data.reduce((sum, item) => sum + (item.referredQuizParticipants || 0), 0);
      const totalCoupons = data.reduce((sum, item) => sum + (item.couponIssuances || 0), 0);

      return {
        totalSent,
        totalClicks,
        clickRate: totalSent > 0 ? (totalClicks / totalSent) * 100 : 0,
        totalReviews,
        reviewRate: totalSent > 0 ? (totalReviews / totalSent) * 100 : 0,
        totalShares,
        shareRate: totalSent > 0 ? (totalShares / totalSent) * 100 : 0,
        totalReferrals,
        totalCoupons,
        couponRate: totalSent > 0 ? (totalCoupons / totalSent) * 100 : 0,
      };
    });

    // THERMAGE 고객 지표 계산
    const thermageMetrics = computed(() => {
      if (!hasThermageData.value) return null;

      const data = certificateHolderData.value;
      const totalSent = data.reduce((sum, item) => sum + (item.thermage?.sent || 0), 0);
      const totalClicks = data.reduce((sum, item) => sum + (item.thermage?.uniqueClicks || 0), 0);
      const totalReviews = data.reduce((sum, item) => sum + (item.thermage?.reviewSubmissions || 0), 0);
      const totalShares = data.reduce((sum, item) => sum + (item.thermage?.shares || 0), 0);
      const totalReferrals = data.reduce((sum, item) => sum + (item.thermage?.referredQuizParticipants || 0), 0);
      const totalCoupons = data.reduce((sum, item) => sum + (item.thermage?.couponIssuances || 0), 0);

      return {
        totalSent,
        totalClicks,
        clickRate: totalSent > 0 ? (totalClicks / totalSent) * 100 : 0,
        totalReviews,
        reviewRate: totalSent > 0 ? (totalReviews / totalSent) * 100 : 0,
        totalShares,
        shareRate: totalSent > 0 ? (totalShares / totalSent) * 100 : 0,
        totalReferrals,
        totalCoupons,
        couponRate: totalSent > 0 ? (totalCoupons / totalSent) * 100 : 0,
      };
    });

    // 후기 작성 퍼널 (ZEISS 컬러 적용)
    const reviewFunnel = computed(() => {
      const data = certificateHolderData.value;
      const sent = metrics.value.totalSent;
      const clicks = metrics.value.totalClicks;
      const eventPageVisits = data.reduce((sum, item) => sum + (item.eventPageVisits || 0), 0);
      const reviewPageLandings = data.reduce((sum, item) => sum + (item.reviewPageLandings || 0), 0);
      const satisfactionInputs = data.reduce((sum, item) => sum + (item.satisfactionInputs || 0), 0);
      const reviews = metrics.value.totalReviews;
      const shares = metrics.value.totalShares;

      const maxValue = sent || 1;

      return [
        {
          label: '메시지 발송',
          value: sent,
          percentage: 100,
          conversionRate: 100,
          color: '#005EB8' // ZEISS Blue
        },
        {
          label: '클릭',
          value: clicks,
          percentage: (clicks / maxValue) * 100,
          conversionRate: sent > 0 ? (clicks / sent) * 100 : 0,
          color: '#1890ff' // Info Blue
        },
        {
          label: '이벤트 페이지',
          value: eventPageVisits,
          percentage: (eventPageVisits / maxValue) * 100,
          conversionRate: clicks > 0 ? (eventPageVisits / clicks) * 100 : 0,
          color: '#20C997' // Success Green
        },
        {
          label: '후기 페이지',
          value: reviewPageLandings,
          percentage: (reviewPageLandings / maxValue) * 100,
          conversionRate: eventPageVisits > 0 ? (reviewPageLandings / eventPageVisits) * 100 : 0,
          color: '#13c2c2' // Cyan
        },
        {
          label: '만족도 입력',
          value: satisfactionInputs,
          percentage: (satisfactionInputs / maxValue) * 100,
          conversionRate: reviewPageLandings > 0 ? (satisfactionInputs / reviewPageLandings) * 100 : 0,
          color: '#8A5FFB' // Purple
        },
        {
          label: '후기 작성',
          value: reviews,
          percentage: (reviews / maxValue) * 100,
          conversionRate: satisfactionInputs > 0 ? (reviews / satisfactionInputs) * 100 : 0,
          color: '#faad14' // Orange
        },
        {
          label: '공유',
          value: shares,
          percentage: (shares / maxValue) * 100,
          conversionRate: reviews > 0 ? (shares / reviews) * 100 : 0,
          color: '#E03131' // Warning Red
        }
      ];
    });

    // 바이럴 계수
    const viralCoefficient = computed(() => {
      const shares = metrics.value.totalShares;
      const referrals = metrics.value.totalReferrals;
      return shares > 0 ? (referrals / shares).toFixed(2) : '0.00';
    });

    // 신규 인증서 발급자 데이터 (certificate 타입)
    const certificateData = computed(() => {
      return props.reportData
        .filter(item => item.messageType === 'certificate')
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    });

    // 기존 인증서 보유자 데이터 (review 타입)
    const reviewData = computed(() => {
      return props.reportData
        .filter(item => item.messageType === 'review')
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    });

    // 상세 테이블 컬럼
    const detailColumns = [
      { title: '날짜', dataIndex: 'date', key: 'date', width: 100, fixed: 'left' },
      { title: '발송', dataIndex: 'sent', key: 'sent', width: 80, align: 'right' },
      { title: '클릭', dataIndex: 'clicks', key: 'clicks', width: 80, align: 'right' },
      { title: '클릭률', dataIndex: 'clickRate', key: 'clickRate', width: 80, align: 'right' },
      { title: '이벤트 유입', dataIndex: 'eventPageVisits', key: 'eventPageVisits', width: 110, align: 'right' },
      { title: '후기 작성', dataIndex: 'reviewSubmissions', key: 'reviewSubmissions', width: 100, align: 'right' },
      { title: '공유', dataIndex: 'shares', key: 'shares', width: 70, align: 'right' },
      { title: '쿠폰 발급', dataIndex: 'couponIssuances', key: 'couponIssuances', width: 100, align: 'right' },
    ];

    // 날짜 클래스
    const getDateClass = (dateStr) => {
      const dayOfWeek = getDayOfWeek(dateStr);
      if (dayOfWeek === 0) return 'date-sunday';
      if (dayOfWeek === 6) return 'date-saturday';
      return '';
    };

    return {
      activeMessageType,
      metrics,
      thermageMetrics,
      hasThermageData,
      reviewFunnel,
      viralCoefficient,
      certificateData,
      reviewData,
      detailColumns,
      formatNumber,
      formatPercentage,
      formatDate,
      getDateClass,
    };
  },
};
</script>

<style scoped>
.certificate-holder-report {
  padding: 24px;
  background: #fafafa;
}

.segment-header {
  margin-bottom: 24px;
  border-radius: 8px;
}

.metrics-section {
  margin-bottom: 24px;
}

.metric-card {
  text-align: center;
  border-radius: 12px;
  border: 2px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  position: relative;
  overflow: hidden;
}

.metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.metric-card.zeiss-blue::before {
  background: linear-gradient(135deg, rgba(0, 94, 184, 0.05) 0%, rgba(0, 94, 184, 0.02) 100%);
}

.metric-card.zeiss-green::before {
  background: linear-gradient(135deg, rgba(32, 201, 151, 0.05) 0%, rgba(32, 201, 151, 0.02) 100%);
}

.metric-card.zeiss-orange::before {
  background: linear-gradient(135deg, rgba(250, 173, 20, 0.05) 0%, rgba(250, 173, 20, 0.02) 100%);
}

.metric-card.zeiss-red::before {
  background: linear-gradient(135deg, rgba(224, 49, 49, 0.05) 0%, rgba(224, 49, 49, 0.02) 100%);
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 94, 184, 0.15);
}

.metric-card.zeiss-blue:hover {
  border-color: #005EB8;
}

.metric-card.zeiss-green:hover {
  border-color: #20C997;
}

.metric-card.zeiss-orange:hover {
  border-color: #faad14;
}

.metric-card.zeiss-red:hover {
  border-color: #E03131;
}

.metric-card:hover::before {
  opacity: 1;
}

:deep(.metric-card .ant-card-body) {
  position: relative;
  z-index: 1;
}

.metric-detail {
  margin-top: 12px;
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.metric-thermage {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  font-size: 12px;
  color: #005EB8;
  font-weight: 600;
}

.kpi-tooltip {
  font-size: 13px;
  line-height: 1.6;
}

.kpi-tooltip p {
  margin: 4px 0;
}

.kpi-tooltip strong {
  color: #005EB8;
  font-size: 14px;
}

:deep(.metric-card .ant-statistic-title) {
  font-size: 14px;
  font-weight: 600;
  color: #595959;
}

:deep(.metric-card .ant-statistic-content-value) {
  font-weight: 700;
}

.funnel-card {
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

:deep(.funnel-card .ant-card-head) {
  background: linear-gradient(to bottom, #fafafa, #fff);
  border-bottom: 1px solid #e8e8e8;
}

:deep(.funnel-card .ant-card-head-title) {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.funnel-horizontal {
  padding: 32px 20px;
  overflow-x: auto;
}

.funnel-steps {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  min-width: max-content;
}

.funnel-step-item {
  display: flex;
  align-items: center;
}

.funnel-step-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 100px;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
}

.funnel-step-box::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
}

.funnel-step-box:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 8px 24px rgba(0, 94, 184, 0.25);
}

.step-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.step-label-text {
  color: white;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 6px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  line-height: 1.3;
}

.step-value-text {
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.funnel-connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 8px;
  position: relative;
}

.connector-line {
  width: 40px;
  height: 2px;
  background: linear-gradient(90deg, rgba(0, 94, 184, 0.3) 0%, rgba(0, 94, 184, 0.6) 100%);
  position: relative;
}

.connector-rate {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f0f5ff;
  padding: 4px 10px;
  border-radius: 12px;
  margin-top: 6px;
  box-shadow: 0 1px 4px rgba(0, 94, 184, 0.1);
}

.rate-value {
  font-size: 11px;
  font-weight: 700;
  color: #005EB8;
}

.rate-arrow {
  font-size: 10px;
  color: #005EB8;
}

.funnel-tooltip {
  font-size: 13px;
  line-height: 1.6;
}

.funnel-tooltip p {
  margin: 4px 0;
}

.funnel-tooltip strong {
  color: #005EB8;
  font-size: 14px;
}

.viral-card,
.reward-card {
  height: 100%;
  border-radius: 12px;
  border: 2px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.viral-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(32, 201, 151, 0.03) 0%, transparent 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.reward-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(250, 173, 20, 0.03) 0%, transparent 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.viral-card:hover,
.reward-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 94, 184, 0.12);
}

.viral-card:hover {
  border-color: #20C997;
}

.reward-card:hover {
  border-color: #faad14;
}

.viral-card:hover::before,
.reward-card:hover::before {
  opacity: 1;
}

:deep(.viral-card .ant-card-head),
:deep(.reward-card .ant-card-head) {
  background: linear-gradient(to bottom, #fafafa, #fff);
  border-bottom: 1px solid #e8e8e8;
}

:deep(.viral-card .ant-card-body),
:deep(.reward-card .ant-card-body) {
  position: relative;
  z-index: 1;
}

.viral-insight {
  padding: 16px;
  background: linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%);
  border-radius: 8px;
  text-align: center;
  border: 1px solid #91d5ff;
}

.insight-label {
  font-weight: 600;
  color: #005EB8;
  margin-right: 8px;
  font-size: 13px;
}

.insight-value {
  font-size: 28px;
  font-weight: 700;
  color: #005EB8;
  margin: 0 8px;
}

.insight-desc {
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 4px;
}

.reward-detail {
  padding: 14px;
  background: linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%);
  border-radius: 8px;
  font-size: 12px;
  color: #595959;
  border: 1px solid #ffe58f;
}

.reward-detail p {
  margin: 6px 0;
  line-height: 1.6;
}

.reward-detail p:first-child {
  margin-top: 0;
}

.reward-detail p:last-child {
  margin-bottom: 0;
}

.detail-card {
  margin-top: 24px;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

:deep(.detail-card .ant-card-head) {
  background: linear-gradient(to bottom, #fafafa, #fff);
  border-bottom: 1px solid #e8e8e8;
}

:deep(.detail-card .ant-card-body) {
  padding: 24px;
}

.date-saturday {
  color: #1890ff;
  font-weight: 600;
}

.date-sunday {
  color: #f5222d;
  font-weight: 600;
}

:deep(.ant-card-head-title) {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
  color: #262626;
  font-size: 13px;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #f5f9ff;
}
</style>
